import React, {
  createContext,
  useContext,
  useState,
  useCallback,
} from "react";
import { BACKEND } from "../utils/api"; // Adjust path if needed

// --- MOCK VIDEOS DATA ---
// Kept here so the Watch component can load them via ID, even on page refresh
const MOCK_VIDEOS = [
  {
    id: 'mock-001', title: 'Building a Full Stack App in 30 Minutes',
    thumbnail_url: 'https://picsum.photos/seed/fullstack/640/360.jpg',
    duration: '28:45', is_live: false, username: 'CodeMaster', userId: 'user-mock-1',
    avatar: 'https://picsum.photos/seed/codemaster/80/80.jpg',
    views: '142K', created_at: new Date(Date.now() - 2 * 3600000).toISOString(),
    // Added video_url for the player
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4' 
  },
  {
    id: 'mock-002', title: 'Top 10 Hidden Features in React 19',
    thumbnail_url: 'https://picsum.photos/seed/react19/640/360.jpg',
    duration: '15:22', is_live: false, username: 'DevInsider', userId: 'user-mock-2',
    avatar: 'https://picsum.photos/seed/devinsider/80/80.jpg',
    views: '89K', created_at: new Date(Date.now() - 5 * 3600000).toISOString(),
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4'
  },
  {
    id: 'mock-003', title: 'Lofi Hip Hop Radio 🎵 Beats to Relax/Study To',
    thumbnail_url: 'https://picsum.photos/seed/lofi/640/360.jpg',
    duration: '0:00', is_live: true, username: 'LofiGirl', userId: 'user-mock-3',
    avatar: 'https://picsum.photos/seed/lofigirl/80/80.jpg',
    views: '24K watching', created_at: new Date(Date.now() - 7200000).toISOString(),
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4'
  },
  {
    id: 'mock-004', title: 'I Survived 100 Days in Hardcore Minecraft',
    thumbnail_url: 'https://picsum.photos/seed/minecraft/640/360.jpg',
    duration: '45:10', is_live: false, username: 'GameLegend', userId: 'user-mock-4',
    avatar: 'https://picsum.photos/seed/gamelegend/80/80.jpg',
    views: '1.2M', created_at: new Date(Date.now() - 24 * 3600000).toISOString(),
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4'
  },
  {
    id: 'mock-005', title: 'Making Perfect Sushi at Home - Beginner Guide',
    thumbnail_url: 'https://picsum.photos/seed/sushi/640/360.jpg',
    duration: '18:33', is_live: false, username: 'ChefKing', userId: 'user-mock-5',
    avatar: 'https://picsum.photos/seed/chefking/80/80.jpg',
    views: '567K', created_at: new Date(Date.now() - 48 * 3600000).toISOString(),
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4'
  },
  {
    id: 'mock-006', title: 'Why AI Will Change Everything by 2026',
    thumbnail_url: 'https://picsum.photos/seed/aifuture/640/360.jpg',
    duration: '22:17', is_live: false, username: 'TechVision', userId: 'user-mock-6',
    avatar: 'https://picsum.photos/seed/techvision/80/80.jpg',
    views: '2.1M', created_at: new Date(Date.now() - 3 * 24 * 3600000).toISOString(),
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4'
  },
  {
    id: 'mock-007', title: 'Morning Yoga Flow for Beginners - 20 Min',
    thumbnail_url: 'https://picsum.photos/seed/yoga/640/360.jpg',
    duration: '20:00', is_live: false, username: 'ZenLife', userId: 'user-mock-7',
    avatar: 'https://picsum.photos/seed/zenlife/80/80.jpg',
    views: '340K', created_at: new Date(Date.now() - 5 * 24 * 3600000).toISOString(),
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4'
  },
  {
    id: 'mock-008', title: 'Epic Mountain Biking in the Alps - POV 4K',
    thumbnail_url: 'https://picsum.photos/seed/bikealps/640/360.jpg',
    duration: '12:48', is_live: false, username: 'ExtremeSports', userId: 'user-mock-8',
    avatar: 'https://picsum.photos/seed/extremesports/80/80.jpg',
    views: '789K', created_at: new Date(Date.now() - 7 * 24 * 3600000).toISOString(),
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/Sintel.mp4'
  }
];

const UnifiedVideoContext = createContext();

export const useUnifiedVideo = () => {
  const context = useContext(UnifiedVideoContext);

  if (!context) {
    throw new Error(
      "useUnifiedVideo must be used within UnifiedVideoProvider"
    );
  }

  return context;
};

export const UnifiedVideoProvider = ({ children }) => {
  const [currentVideo, setCurrentVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  const [isMiniPlayer, setIsMiniPlayer] = useState(false);
  
  // New states for loading videos by ID
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(null);

  // NEW: Fetches video by ID (handles both mock and real API)
  const loadVideoById = useCallback(async (videoId) => {
    if (!videoId) return;
    
    setVideoLoading(true);
    setVideoError(null);

    try {
      let videoData = null;

      // 1. Check if it's a mock video
      if (videoId.startsWith('mock-')) {
        // Simulate network delay for realistic loading spinner
        await new Promise(resolve => setTimeout(resolve, 400));
        videoData = MOCK_VIDEOS.find(v => v.id === videoId);
        if (!videoData) throw new Error("Mock video not found");
      } 
      // 2. Fetch from real API
      else {
        const token = localStorage.getItem('mintza_token');
        const res = await fetch(`${BACKEND}/api/videos/${videoId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (!res.ok) throw new Error("Failed to load video");
        const json = await res.json();
        videoData = json.data || json.video || json;
      }

      setCurrentVideo(videoData);
      setIsPlaying(true);
    } catch (err) {
      console.error("Error loading video:", err);
      setVideoError(err.message || "Failed to load video");
    } finally {
      setVideoLoading(false);
    }
  }, []);

  const playVideo = useCallback((video) => {
    setCurrentVideo(video);
    setIsPlaying(true);
  }, []);

  const pauseVideo = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const stopVideo = useCallback(() => {
    setCurrentVideo(null);
    setIsPlaying(false);
    setIsMiniPlayer(false);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const value = {
    // Video State
    currentVideo,
    setCurrentVideo,

    // Playback State
    isPlaying,
    setIsPlaying,

    // Audio State
    volume,
    setVolume,
    isMuted,
    setIsMuted,
    toggleMute,

    // Mini Player
    isMiniPlayer,
    setIsMiniPlayer,

    // Actions
    playVideo,
    pauseVideo,
    stopVideo,
    
    // URL Loading Actions (NEW)
    loadVideoById,
    videoLoading,
    videoError,
  };

  return (
    <UnifiedVideoContext.Provider value={value}>
      {children}
    </UnifiedVideoContext.Provider>
  );
};

export default UnifiedVideoContext;
